import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ChatThread, { ChatAvatar } from "./ChatThread";
import cs from "./chat.module.css";
import clinicAPI from "../../../services/clinicApi";
import { personDisplayName, trainerPersonalChatTitle } from "../../../utils/clientAppUtils";

function BackChevron() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14.5 6.5L9 12l5.5 5.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function resolveHeaderPhoto(meta, threadType, mode) {
  if (!meta) return null;
  if (threadType === "member") return meta.peer_photo_url || null;
  if (mode === "staff" && meta.client?.photo_url) return meta.client.photo_url;
  if (mode === "client" && threadType === "direct") {
    const trainerPhoto = meta.trainers?.find((t) => t.photo_url)?.photo_url;
    return trainerPhoto || null;
  }
  return null;
}

export default function ChatConversationPage({
  title,
  subtitle,
  backTo,
  threadType = "direct",
  mode = "staff",
  patientId,
  peerUserId,
  groupId,
  athleteQueryParam = {},
  fullscreen = true,
  avatarLabel,
  avatarUrl,
  onMeta,
  hideBack = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const initialDraft = location.state?.draft || "";
  const [displayTitle, setDisplayTitle] = useState(title);
  const [displayPhotoUrl, setDisplayPhotoUrl] = useState(avatarUrl || null);

  useEffect(() => {
    setDisplayTitle(title);
    setDisplayPhotoUrl(avatarUrl || null);
  }, [title, avatarUrl, peerUserId]);

  useEffect(() => {
    if (threadType !== "member" || !peerUserId || mode !== "client") return undefined;
    if (title && title !== "Участник") return undefined;
    let cancelled = false;
    clinicAPI
      .getMyMemberChat(peerUserId, { mark_read: "0" })
      .then((data) => {
        if (cancelled) return;
        if (data?.meta?.peer_name) {
          setDisplayTitle(personDisplayName(data.meta.peer_name) || data.meta.peer_name);
        }
        if (data?.meta?.peer_photo_url) {
          setDisplayPhotoUrl(data.meta.peer_photo_url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [threadType, peerUserId, title, mode]);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const cls = mode === "client" ? "client-chat-open" : "admin-chat-open";
    document.documentElement.classList.add(cls);
    return () => document.documentElement.classList.remove(cls);
  }, [fullscreen, mode]);

  const shellClass = fullscreen
    ? mode === "client"
      ? `${cs.chatScreenPage} chatScreenPage`
      : `${cs.chatScreenAdmin} chatScreenAdmin`
    : `${cs.chatHubThread} chatHubThread`;

  const handleBack = () => {
    if (backTo) navigate(backTo);
    else navigate(-1);
  };

  const isClub = threadType === "club";
  const isGroup = threadType === "group";

  return (
    <div className={shellClass}>
      <header className={cs.chatHeader}>
        {!hideBack ? (
          <button
            type="button"
            className={cs.chatHeaderBack}
            onClick={handleBack}
            aria-label="Назад"
          >
            <BackChevron />
          </button>
        ) : null}
        {displayPhotoUrl || avatarLabel || isClub || isGroup ? (
          <ChatAvatar
            photoUrl={displayPhotoUrl}
            name={displayTitle || avatarLabel}
            club={isClub}
            group={isGroup}
            className={cs.chatHeaderAvatar}
            imgClassName={cs.chatHeaderAvatarImg}
          />
        ) : null}
        <div className={cs.chatHeaderMain}>
          <h1 className={cs.chatHeaderTitle}>{displayTitle}</h1>
          {subtitle ? <p className={cs.chatHeaderSub}>{subtitle}</p> : null}
        </div>
      </header>
      <ChatThread
        mode={mode}
        threadType={threadType}
        patientId={patientId}
        peerUserId={peerUserId}
        groupId={groupId}
        athleteQueryParam={athleteQueryParam}
        variant="page"
        initialDraft={initialDraft}
        onMeta={(meta) => {
          if (threadType === "member" && meta?.peer_name) {
            const isStaffPeer = ["owner", "admin", "trainer"].includes(
              meta.peer_role,
            );
            const formatted = isStaffPeer
              ? trainerPersonalChatTitle(meta.peer_name)
              : personDisplayName(meta.peer_name);
            setDisplayTitle(formatted || meta.peer_name);
          }
          const photo = resolveHeaderPhoto(meta, threadType, mode);
          if (photo) setDisplayPhotoUrl(photo);
          onMeta?.(meta);
        }}
      />
    </div>
  );
}
